/** Отступ от верха при скролле к секциям деталки (табы навигации, хеш-ссылки). */
export const SECTION_SCROLL_OFFSET: number = 70;

const documentTop = (el: Element): number =>
  el.getBoundingClientRect().top + window.scrollY;

/**
 * Ждёт, пока положение элемента в документе перестанет меняться
 * (выше него догружаются async-секции и картинки), но не дольше timeout.
 */
export const waitForStableOffset = (
  el: Element,
  stableMs: number = 400,
  timeout: number = 3000,
  interval: number = 50,
): Promise<void> =>
  new Promise((resolve) => {
    const startedAt = Date.now();
    let lastTop = documentTop(el);
    let stableSince = startedAt;
    const check = (): void => {
      const top = documentTop(el);
      if (Math.abs(top - lastTop) > 1) {
        lastTop = top;
        stableSince = Date.now();
      }
      if (Date.now() - stableSince >= stableMs || Date.now() - startedAt >= timeout) {
        resolve();
        return;
      }
      setTimeout(check, interval);
    };
    setTimeout(check, interval);
  });

/** Ждёт окончания плавного скролла (scrollend), с таймаутом для браузеров без события. */
export const waitForScrollEnd = (timeout: number = 1000): Promise<void> =>
  new Promise((resolve) => {
    const done = (): void => {
      window.removeEventListener('scrollend', done);
      resolve();
    };
    window.addEventListener('scrollend', done, { once: true });
    setTimeout(done, timeout);
  });

export const waitForScrollIdle = (
  idleMs: number = 150,
  timeout: number = 2000,
  interval: number = 50,
): Promise<void> =>
  new Promise((resolve) => {
    const startedAt = Date.now();
    let lastScrollY = window.scrollY;
    let idleSince = startedAt;
    const check = (): void => {
      if (Math.abs(window.scrollY - lastScrollY) > 0.5) {
        lastScrollY = window.scrollY;
        idleSince = Date.now();
      }
      if (Date.now() - idleSince >= idleMs || Date.now() - startedAt >= timeout) {
        resolve();
        return;
      }
      setTimeout(check, interval);
    };
    setTimeout(check, interval);
  });

/**
 * При загрузке страницы с хешем Chrome сам повторно якорит фрагмент (без отступа)
 * при каждом сдвиге вёрстки до события load — программный скролл до этого момента перебьётся.
 */
export const waitForWindowLoad = (): Promise<void> =>
  new Promise((resolve) => {
    if (document.readyState === 'complete') {
      resolve();
      return;
    }
    window.addEventListener('load', () => resolve(), { once: true });
  });

const USER_SCROLL_EVENTS: string[] = ['wheel', 'touchstart', 'keydown'];
const SECTION_SETTLE_QUIET_MS: number = 3000;
const SECTION_SETTLE_MAX_MS: number = 10000;
const SECTION_SETTLE_INTERVAL_MS: number = 50;
const SECTION_ALIGN_TOLERANCE: number = 2;

const maxScrollTop = (): number =>
  document.documentElement.scrollHeight - window.innerHeight;

const alignedScrollTop = (el: Element, offset: number): number => {
  const top = documentTop(el) - offset;
  const max = maxScrollTop();
  return max > 0 ? Math.min(top, max) : top;
};

const resolveConnected = (el: Element): Element | null => {
  if (el.isConnected || !el.id) return el;
  return document.getElementById(el.id);
};

const keepSectionAligned = async (
  el: Element,
  offset: number,
  shouldStop: () => boolean,
  destination: number,
  quietMs: number,
  maxMs: number,
): Promise<void> => {
  const startedAt = Date.now();
  let quietSince = startedAt;
  let target: Element = el;
  let expectedScrollY = destination;

  while (Date.now() - startedAt < maxMs && Date.now() - quietSince < quietMs) {
    if (shouldStop()) return;

    const connected = resolveConnected(target);
    if (!connected) return;
    target = connected;

    const top = alignedScrollTop(target, offset);
    if (Math.abs(window.scrollY - top) > SECTION_ALIGN_TOLERANCE) {
      const isScrolledAway =
        Math.abs(window.scrollY - expectedScrollY) > SECTION_ALIGN_TOLERANCE;
      if (isScrolledAway) return;
      window.scrollTo({ top, behavior: 'auto' });
      quietSince = Date.now();
    }
    expectedScrollY = window.scrollY;

    await new Promise((resolve) => setTimeout(resolve, SECTION_SETTLE_INTERVAL_MS));
  }
};

/**
 * Плавно скроллит к секции с отступом, дождавшись стабилизации вёрстки,
 * и после скролла поправляет позицию, если контент выше секции успел сдвинуться.
 * Если пользователь начал скроллить сам или isActive() вернул false — не вмешивается.
 */
export const scrollToSection = async (
  el: Element,
  offset: number = SECTION_SCROLL_OFFSET,
  isActive: () => boolean = () => true,
  settleMs: number = SECTION_SETTLE_QUIET_MS,
  maxSettleMs: number = SECTION_SETTLE_MAX_MS,
): Promise<void> => {
  let isUserScrolling = false;
  const markUserScroll = (): void => {
    isUserScrolling = true;
  };
  const shouldStop = (): boolean => isUserScrolling || !isActive();
  USER_SCROLL_EVENTS.forEach((event) =>
    window.addEventListener(event, markUserScroll, { passive: true }),
  );

  try {
    await waitForWindowLoad();
    await waitForStableOffset(el);
    if (shouldStop()) return;
    const destination = alignedScrollTop(el, offset);
    scroll(el, offset);

    await waitForScrollEnd();
    await waitForScrollIdle();

    await keepSectionAligned(el, offset, shouldStop, destination, settleMs, maxSettleMs);
  } finally {
    USER_SCROLL_EVENTS.forEach((event) =>
      window.removeEventListener(event, markUserScroll),
    );
  }
};

export const scroll = (el: Element | null, customOffset: number = 0): void => {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - customOffset;
  window.scrollTo({ top, behavior: 'smooth' });
};

export const scrollToTop = (behavior: ScrollBehavior = 'smooth'): void => {
  window.scrollTo({ top: 0, behavior });
};

export const scrollToElementBottom = (
  el: Element | null,
  offsetFromBottom: number = 0,
): void => {
  if (!el) return;
  const top =
    el.getBoundingClientRect().bottom +
    window.scrollY -
    window.innerHeight +
    offsetFromBottom;
  window.scrollTo({ top, behavior: 'smooth' });
};
